import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { FormModal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { PhoneField } from '@/components/forms';
import { ApiError, UnauthorizedError } from '@/lib/api/errors';
import { isUsPhone } from '@/lib/validation/phone';
import type { ValidationDetail } from '@/lib/validation/server-details';

import { sendNewPhoneOtp } from '../api';
import { DEALER_PROFILE_COPY, PROFILE_COPY, PROFILE_ERROR_CODES } from '../constants';
import { useIsDealer } from '../hooks';
import type { ProfileOtpDispatch } from '../schemas';

import otpModal from './otp-modal.module.scss';

type Rejected = { phone: string; message: string };

type NewNumberStepProps = {
  /** Set when Step 3 bounced back on the race (§7f): the number and its error come back together. */
  initial?: Rejected;
  onSent: (dispatch: ProfileOtpDispatch, phone: string) => void;
  /** 403 PHONE_VERIFY_REQUIRED — the Step-1 gate lapsed (grilling Q2). */
  onExpired: () => void;
  onCancel: () => void;
};

const COPY = PROFILE_COPY.changePhone;
type NumberForm = { phone: string };

/** 117854 Step 2. No resolver: §6e judges on Send Code, and RHF's post-submit re-validation would
 *  re-raise the error on every keystroke against §6i. */
export function NewNumberStep({ initial, onSent, onExpired, onCancel }: NewNumberStepProps) {
  const toast = useToast();
  const isDealer = useIsDealer();
  const mutation = useMutation({ mutationFn: sendNewPhoneOtp });
  const form = useForm<NumberForm>({ defaultValues: { phone: initial?.phone ?? '' } });
  const phone = useWatch({ control: form.control, name: 'phone' }) ?? '';
  const [rejected, setRejected] = useState<Rejected | null>(initial ?? null);

  // §6i: an error belongs to the digits it judged (ticket 03's pattern) — any edit drops it.
  if (rejected && rejected.phone !== phone) setRejected(null);
  const { setError, clearErrors } = form;
  useEffect(() => {
    if (rejected) setError('phone', { message: rejected.message });
    else clearErrors('phone');
  }, [rejected, setError, clearErrors]);

  const onSubmit = async ({ phone: value }: NumberForm) => {
    const reject = (message: string) => setRejected({ phone: value, message });
    if (!isUsPhone(value)) return reject(COPY.newNumber.invalid); // §6e; NANP, ADR-0035
    try {
      onSent(await mutation.mutateAsync(value), value);
    } catch (error) {
      if (error instanceof UnauthorizedError) return; // sessionExpiry owns it (§14)
      if (!(error instanceof ApiError)) {
        toast.error({ title: PROFILE_COPY.genericError, key: 'profile-otp' });
        return;
      }
      if (error.code === PROFILE_ERROR_CODES.phoneVerifyRequired) return onExpired();
      if (error.code === PROFILE_ERROR_CODES.phoneSameAsCurrent)
        return reject(COPY.newNumber.sameAsCurrent);
      if (error.code === PROFILE_ERROR_CODES.phoneAlreadyExists)
        return reject(COPY.newNumber.alreadyInUse);
      if (
        error.code === PROFILE_ERROR_CODES.validationFailed &&
        Array.isArray(error.details) &&
        (error.details as ValidationDetail[]).some((d) => d.field === 'phone')
      ) {
        return reject(COPY.newNumber.invalid);
      }
      if (error.code === PROFILE_ERROR_CODES.resendRateLimitExceeded) {
        toast.warning({
          title: isDealer ? DEALER_PROFILE_COPY.rateLimited : PROFILE_COPY.rateLimited,
          key: 'profile-otp',
        });
        return;
      }
      toast.error({ title: PROFILE_COPY.genericError, key: 'profile-otp' });
    }
  };

  return (
    <FormModal
      open
      form={form}
      onSubmit={onSubmit}
      onClose={onCancel}
      title={COPY.newNumberTitle}
      subtitle={<span className={otpModal.helper}>{COPY.newNumber.helper}</span>}
      width={456}
      className={otpModal.modal}
      submitText={PROFILE_COPY.sendCode}
      primaryDisabled={phone.length === 0 || rejected !== null} // §6b; Figma greys it under an error
      maskClosable={false} // §8 names Cancel, ✕ and Escape; a stray click must not drop the number
    >
      <PhoneField
        name="phone"
        control={form.control}
        label="Phone Number"
        required
        placeholder={COPY.newNumber.placeholder}
        autoFocus
      />
    </FormModal>
  );
}
